import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doctorMe } from '../services/doctorApi';
import { maybeFeedbackMilestone } from '../utils/doctorFeedbackMilestones';

const DoctorSessionContext = createContext(null);

export function DoctorSessionProvider({ children }) {
  const { isAuthenticated, accountType } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [draftNote, setDraftNote] = useState(null);
  const [feedbackPrompt, setFeedbackPrompt] = useState(null);

  const refreshDoctor = useCallback(async () => {
    const me = await doctorMe();
    setDoctor(me);
    return me;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || accountType !== 'doctor') {
      setDoctor(null);
      setLoading(false);
      setFeedbackPrompt(null);
      return undefined;
    }

    setLoading(true);
    (async () => {
      try {
        const me = await doctorMe();
        if (!cancelled) setDoctor(me);
      } catch {
        if (!cancelled) setDoctor(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountType, isAuthenticated]);

  const showToast = useCallback((message) => {
    setToast({ message, id: Date.now() });
  }, []);

  const clearToast = useCallback(() => setToast(null), []);

  const promptFeedbackIfNeeded = useCallback(
    (approvedNotesCount) => {
      const doctorId = doctor?.id;
      const milestone = maybeFeedbackMilestone(approvedNotesCount, doctorId);
      if (milestone != null) {
        setFeedbackPrompt({ milestone });
      }
    },
    [doctor?.id]
  );

  const clearFeedbackPrompt = useCallback(() => setFeedbackPrompt(null), []);

  const trialRemaining = doctor
    ? Math.max(0, (doctor.trial_notes_limit || 20) - (doctor.trial_notes_used || 0))
    : 0;

  const trialExhausted =
    doctor?.subscription_status === 'trial' && trialRemaining <= 0;

  const value = useMemo(
    () => ({
      doctor,
      setDoctor,
      loading,
      refreshDoctor,
      toast,
      showToast,
      clearToast,
      draftNote,
      setDraftNote,
      trialRemaining,
      trialExhausted,
      feedbackPrompt,
      promptFeedbackIfNeeded,
      clearFeedbackPrompt,
    }),
    [
      doctor,
      loading,
      refreshDoctor,
      toast,
      showToast,
      clearToast,
      draftNote,
      trialRemaining,
      trialExhausted,
      feedbackPrompt,
      promptFeedbackIfNeeded,
      clearFeedbackPrompt,
    ]
  );

  return (
    <DoctorSessionContext.Provider value={value}>{children}</DoctorSessionContext.Provider>
  );
}

export function useDoctorSession() {
  const ctx = useContext(DoctorSessionContext);
  if (!ctx) throw new Error('useDoctorSession must be used within DoctorSessionProvider');
  return ctx;
}
